import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Phone,
  Shield,
  Sparkles,
  CheckCircle2,
  MapPin,
} from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ChatBot from "../components/ChatBot";
import FloatingCallButton from "../components/FloatingCallButton";
import SEOHead from "../components/SEOHead";
import { SERVICE_PAGES } from "../data/servicePages";

const TRUST_POINTS = [
  { icon: Shield, label: "Garantie décennale" },
  { icon: Sparkles, label: "Devis gratuit sous 48h" },
  { icon: CheckCircle2, label: "Note Google 5/5" },
  { icon: MapPin, label: "Basés à Valenciennes" },
];

export default function ServicePage({ pageKey }) {
  const cfg = SERVICE_PAGES[pageKey];

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  }, [cfg]);

  if (!cfg) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <p>Page introuvable.</p>
      </div>
    );
  }

  return (
    <div
      data-testid={`service-page-${pageKey}`}
      className="bg-[#050505] text-white min-h-screen"
    >
      <SEOHead title={cfg.title} description={cfg.description} path={cfg.path} />
      <Nav />

      <main>
        {/* HERO */}
        <section className="relative pt-32 pb-16 md:pt-44 md:pb-24 border-b border-white/5">
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 30% 20%, rgba(255,90,0,0.18) 0%, transparent 55%)",
            }}
          />
          <div className="relative max-w-5xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="eyebrow mb-5 inline-flex items-center gap-2">
                <MapPin size={12} />
                <span>{cfg.eyebrow}</span>
              </div>
              <h1
                data-testid="service-page-h1"
                className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6"
              >
                {cfg.h1}
              </h1>
              <p className="text-neutral-300 text-base md:text-lg leading-relaxed max-w-3xl mb-8">
                {cfg.intro}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/devis"
                  data-testid="service-cta-devis"
                  className="btn-primary justify-center text-base px-6 py-3.5"
                >
                  Demander un devis gratuit <ArrowRight size={16} />
                </Link>
                <a
                  href="tel:+33763433867"
                  data-testid="service-cta-phone"
                  className="btn-ghost justify-center"
                >
                  <Phone size={16} /> 07 63 43 38 67
                </a>
              </div>

              {/* Trust badges */}
              <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
                {TRUST_POINTS.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 text-xs text-neutral-300 bg-white/[0.03] border border-white/10 px-3 py-2.5 rounded-xl"
                  >
                    <Icon size={14} className="text-[#FF5A00] shrink-0" />
                    <span className="leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CONTENT SECTIONS */}
        <section className="py-20 md:py-28">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="space-y-12 md:space-y-16">
              {cfg.sections.map((s, i) => (
                <motion.article
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  data-testid={`service-section-${i}`}
                >
                  <h2 className="font-display text-2xl md:text-3xl text-white mb-4 leading-tight">
                    <span className="text-[#FF5A00] mr-3">{String(i + 1).padStart(2, "0")}.</span>
                    {s.title}
                  </h2>
                  <p className="text-neutral-300 text-base md:text-[17px] leading-[1.75]">
                    {s.body}
                  </p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 md:py-28 border-t border-white/5 bg-gradient-to-b from-transparent to-[#0a0a0a]">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
              Prêt à transformer votre façade ?
            </h2>
            <p className="text-neutral-400 mb-8 max-w-xl mx-auto">
              Visite technique offerte et devis détaillé sous 48h. Sans engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/devis"
                data-testid="service-bottom-cta-devis"
                className="btn-primary justify-center text-base px-8 py-4"
              >
                Demander mon devis gratuit <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+33763433867"
                data-testid="service-bottom-cta-phone"
                className="btn-ghost justify-center"
              >
                <Phone size={16} /> 07 63 43 38 67
              </a>
            </div>
          </div>
        </section>

        {/* RELATED SERVICES (interlinking SEO) */}
        <section className="py-16 border-t border-white/5">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <h2 className="font-display text-xl text-white mb-5">
              Autres prestations dans le Nord (59)
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.values(SERVICE_PAGES)
                .filter((p) => p.path !== cfg.path)
                .slice(0, 6)
                .map((p) => (
                  <Link
                    key={p.path}
                    to={p.path}
                    className="block p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#FF5A00]/40 hover:bg-white/[0.04] transition-colors text-sm text-white leading-snug"
                  >
                    <span className="block text-[#FF5A00] text-[10px] uppercase tracking-wider mb-1">
                      {p.eyebrow.split("·")[1]?.trim() || "GMC"}
                    </span>
                    {p.h1}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ChatBot />
      <FloatingCallButton />
    </div>
  );
}
